import TabPanel from 'devextreme-react/tab-panel';
import { equals } from 'ramda';
import React, { useEffect, useState } from 'react';
import { Prompt, useLocation, useParams } from 'react-router-dom';
import { useLastLocation } from 'react-router-last-location';
import useSWR, { SWRConfiguration } from 'swr';
import { Loader } from '../../components/loader/Loader';
import { useAssetClass } from '../../contexts/assetClass';
import { useAuth } from '../../contexts/auth';
import { useBreadcrumbs } from '../../contexts/breadcrumbsContext';
import { useModuleEnabled } from '../../contexts/moduleContext';
import { useTranslation } from '../../contexts/translation';
import { calculateAttachement } from '../../functions/counters';
import { PageContentWrapper } from '../../layouts/contentWrapper/PageContentWrapper';
import { UserRoles } from '../../types/applicationUser.types';
import { Asset } from '../../types/asset.types';
import { NfcTag } from '../../types/assignTagToAsset.types';
import { TabLocationState } from '../../types/locationState.types';
import { DetailPageParams } from '../../types/pageParams.types';
import { AssetSpareParts } from '../../types/spareParts.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { STORAGE_KEYS } from '../../utils/localStorageKeys';
import { AssetTabChildren } from './AssetTabs/AssetTabChildren';
import { AssetTabCycle } from './AssetTabs/AssetTabCycle';
import { AssetTabFiles } from './AssetTabs/AssetTabFiles';
import { AssetTabGeneral } from './AssetTabs/AssetTabGeneral';
import { AssetTabMaintenanceCosts } from './AssetTabs/AssetTabMaintenanceCosts';
import { AssetTabNfcTags } from './AssetTabs/AssetTabNfcTags';
import { AssetTabParameterMeasured } from './AssetTabs/AssetTabParameterMeasured';
import { AssetTabParameters } from './AssetTabs/AssetTabParameters';
import { AssetTabResponsiblePersons } from './AssetTabs/AssetTabResponsiblePersons';
import { AssetTabRevisionCycle } from './AssetTabs/AssetTabRevisionCycle';
import { AssetTabSpareParts } from './AssetTabs/AssetTabSpareParts';
import { AssetTabTasks } from './AssetTabs/AssetTabTasks';

export interface ITab {
  currentTab?: number;
  tab?: number;
  active: boolean;
}

export const swrConfigNoMount: SWRConfiguration = {
  revalidateOnMount: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  refreshInterval: 0,
};
export const swrConfig = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  refreshInterval: 0,
};

export const assetEditSwrConfig = {
  revalidateOnMount: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  refreshInterval: 0,
};

export function AssetsEdit() {
  const { translate } = useTranslation();
  const { isSparePartEnabled, isNfcEnabled, isMaintenanceCostEnabled } = useModuleEnabled();
  const transFormName = 'Asset.Tabs';
  const { id } = useParams<DetailPageParams>();
  const { state }: { state: TabLocationState | undefined } = useLocation();
  const { user } = useAuth();
  const filesEntity = 'Asset';
  const canEdit = user?.roles.includes(UserRoles.AssetEdit);
  const canCreateTask = user?.roles.includes(UserRoles.TaskCreate);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [assetDataChanged, setAssetDataChanged] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<number>(state?.defaultTab ?? 0);
  const { assetClass } = useAssetClass();
  // const [asset] = useItemDataApi<Asset>(API_URL.ASSET_ITEM, id);
  const { setValue } = useBreadcrumbs();

  const lastLocation = useLastLocation();
  const location = useLocation();

  useEffect(() => {
    setValue({
      pageName: 'Edit asset',
      steps: [
        { name: 'Home', pathToCutCount: 3 },
        { name: 'Assets', pathToCutCount: 2 },
      ],
    });
    return () => {
      setValue(null);
    };
  }, []);

  const {
    data: asset,
    error: errorAsset,
    mutate: mutateAsset,
  } = useSWR<Asset>(`${API_URL.ASSET_ITEM}?id=${id}`, fetcher, swrConfig);

  const {
    data: spareParts,
    error: errorSpareParts,
    mutate: mutateSpareParts,
  } = useSWR<AssetSpareParts[]>(
    isSparePartEnabled
      ? `${API_URL.SPARE_PART_ISSUED_LIST_AGGREGATED_FOR_ASSET}?assetId=${id}`
      : null,
    fetcher,
    swrConfig,
  );

  const {
    data: nfcTags,
    error: errorNfcTags,
    mutate: mutateNfcTags,
  } = useSWR<NfcTag[]>(
    isNfcEnabled ? `${API_URL.NFC_TAG_LIST}?parentId=${id}` : null,
    fetcher,
    swrConfig,
  );

  // Counters
  const { data: assetChildCount } = useSWR<number>(
    `${API_URL.ASSET_COUNT}?parentId=${id}`,
    fetcher,
    swrConfig,
  );
  const { data: filesCount, mutate: mutateAttachmentCount } = useSWR<number>(
    `${API_URL.FILE_COUNT}?entity=${filesEntity}&entityId=${id}`,
    fetcher,
    swrConfig,
  );
  const { data: filesRelativesCount, mutate: mutateAttachmentRelativesCount } = useSWR<number>(
    `${API_URL.FILE_COUNT_BY_ASSET}?assetId=${id}`,
    fetcher,
    swrConfig,
  );
  const { data: hyperlinksCount, mutate: mutateHyperlinksCount } = useSWR<number>(
    `${API_URL.HYPERLINK_COUNT}?entity=${filesEntity}&entityId=${id}`,
    fetcher,
    swrConfig,
  );
  const { data: hyperlinksRelativesCount, mutate: mutateHyperlinksRelativesCount } = useSWR<number>(
    `${API_URL.HYPERLINK_COUNT_BY_ASSET}?assetId=${id}`,
    fetcher,
    swrConfig,
  );
  const { data: assetParametersCount } = useSWR<number>(
    `${API_URL.ASSET_PARAMETER_COUNT}?parentId=${id}`,
    fetcher,
    swrConfig,
  );
  const { data: assetCyclesCount } = useSWR<number>(
    `${API_URL.ASSET_CYCLE_COMMON_COUNT}?parentId=${id}`,
    fetcher,
    swrConfig,
  );
  const { data: assetRevisionCyclesCount } = useSWR<number>(
    `${API_URL.ASSET_CYCLE_REVISION_COUNT}?parentId=${id}`,
    fetcher,
    swrConfig,
  );
  const { data: tasksCount } = useSWR<number>(
    `${API_URL.TASK_ASSET_COUNT}?assetId=${id}`,
    fetcher,
    swrConfig,
  );
  const { data: assetParametersMeasuredCount } = useSWR<number>(
    `${API_URL.ASSET_PARAMETER_MEASURED_COUNT}?assetId=${id}`,
    fetcher,
    swrConfig,
  );
  const { data: assetResponsiblePersonsCount } = useSWR<number>(
    `${API_URL.ASSET_RESPONSIBLE_PERSON_COUNT}?parentId=${id}`,
    fetcher,
    swrConfig,
  );
  const { data: maintenanceCostsCount, mutate: mutateMaintenanceCostsCount } = useSWR<number>(
    isMaintenanceCostEnabled ? `${API_URL.MAINTENANCE_COST_COUNT}?parentId=${id}` : null,
    fetcher,
    swrConfig,
  );

  // otevřeno v nové záložce
  useEffect(() => {
    // nastavení current tab z session storage
    const hasPreviousLocation = lastLocation?.pathname !== location.pathname;
    const valueFromStorage = localStorage.getItem(STORAGE_KEYS.CONTEXT_STATE);
    if (hasPreviousLocation && lastLocation?.pathname !== '/login')
      return localStorage.removeItem(STORAGE_KEYS.CONTEXT_STATE);
    if (!valueFromStorage || valueFromStorage === 'null' || hasPreviousLocation) return;
    const obj = valueFromStorage ? JSON.parse(valueFromStorage) : null;
    if (obj?.tab) setCurrentTab(obj.tab);
  }, [lastLocation]);

  /**
   * Definice záložek. Viditelnost je řízená moduly a oprávněním, výsledný index se dopočítá
   * až po odfiltrování skrytých - DevExtreme TabPanel skryté položky nevykresluje a onTitleClick
   * vrací index vykreslené položky. Nové záložky se proto přidávají na konec, aby se nezměnily
   * indexy odkazů z obecných reportů (link.defaultTab).
   */
  const tabDefinitions = [
    {
      title: translate!('General data', transFormName),
      render: (index: number) => (
        <AssetTabGeneral
          isEdit
          isReadonly={!canEdit}
          setAssetDataChanged={setAssetDataChanged}
          active={equals(currentTab, index)}
          currentTab={currentTab}
          tab={index}
          mutateData={mutateAsset}
        />
      ),
    },
    {
      title: `${translate!('Child assets', transFormName)} (${assetChildCount ?? 0})`,
      render: (index: number) => (
        <AssetTabChildren
          isReadonly={!canEdit}
          assetClassId={assetClass}
          assetId={asset?.id}
          active={equals(currentTab, index)}
          currentTab={currentTab}
          tab={index}
        />
      ),
    },
    {
      title: `${translate!('Files', transFormName)} (${calculateAttachement(
        filesCount,
        hyperlinksCount,
        filesRelativesCount,
        hyperlinksRelativesCount,
      )})`,
      render: (index: number) => (
        <AssetTabFiles
          isReadonly={!canEdit}
          currentTab={currentTab}
          tab={index}
          active={equals(currentTab, index)}
          onFileCountChange={() => {
            mutateAttachmentCount();
            mutateAttachmentRelativesCount();
            mutateHyperlinksCount();
            mutateHyperlinksRelativesCount();
          }}
        />
      ),
    },
    {
      title: `${translate!('Parameters', transFormName)} (${assetParametersCount ?? 0})`,
      render: (index: number) => (
        <AssetTabParameters
          isReadonly={!canEdit}
          currentTab={currentTab}
          tab={index}
          active={equals(currentTab, index)}
        />
      ),
    },
    {
      title: `${translate!('Cycles', transFormName)} (${assetCyclesCount ?? 0})`,
      render: (index: number) => (
        <AssetTabCycle
          isReadonly={!canEdit}
          canCreateTask={canCreateTask!}
          currentTab={currentTab}
          tab={index}
          active={equals(currentTab, index)}
        />
      ),
    },
    {
      title: `${translate!('Revision cycles', transFormName)} (${assetRevisionCyclesCount ?? 0})`,
      render: (index: number) => (
        <AssetTabRevisionCycle
          isReadonly={!canEdit}
          canCreateTask={canCreateTask!}
          currentTab={currentTab}
          tab={index}
          active={equals(currentTab, index)}
        />
      ),
    },
    {
      title: `${translate!('Measuring', transFormName)} (${assetParametersMeasuredCount ?? 0})`,
      render: (index: number) => (
        <AssetTabParameterMeasured
          isReadonly={!canEdit}
          currentTab={currentTab}
          tab={index}
          active={equals(currentTab, index)}
        />
      ),
    },
    {
      title: `${translate!('Responsible persons', transFormName)} (${
        assetResponsiblePersonsCount ?? 0
      })`,
      render: (index: number) => (
        <AssetTabResponsiblePersons
          isReadonly={!canEdit}
          active={equals(currentTab, index)}
          currentTab={currentTab}
          tab={index}
        />
      ),
    },
    {
      title: `${translate!('Tasks', transFormName)} (${tasksCount})`,
      render: (index: number) => (
        <AssetTabTasks
          isReadonly={!canEdit}
          active={equals(currentTab, index)}
          currentTab={currentTab}
          tab={index}
        />
      ),
    },
    {
      visible: !!isSparePartEnabled,
      title: `${translate!('Spare parts', transFormName)} (${spareParts?.length})`,
      render: (index: number) =>
        spareParts && (
          <AssetTabSpareParts
            active={equals(currentTab, index)}
            currentTab={currentTab}
            spareParts={spareParts}
            mutateSpareParts={mutateSpareParts}
            tab={index}
          />
        ),
    },
    {
      visible: !!isNfcEnabled,
      title: `${translate!('Tags', transFormName)} (${nfcTags?.length})`,
      render: (index: number) =>
        nfcTags && (
          <AssetTabNfcTags
            active={equals(currentTab, index)}
            currentTab={currentTab}
            nfcTags={nfcTags}
            mutateNfcTags={mutateNfcTags}
            tab={index}
            isReadOnly={!canEdit}
          />
        ),
    },
    {
      // #737 - jen při aktivním modulu a s oprávněním na čtení strojů
      visible: !!isMaintenanceCostEnabled,
      title: `${translate!('Maintenance costs', transFormName)} (${maintenanceCostsCount ?? 0})`,
      render: (index: number) => (
        <AssetTabMaintenanceCosts
          isReadonly={!canEdit}
          active={equals(currentTab, index)}
          currentTab={currentTab}
          tab={index}
          onCostCountChange={mutateMaintenanceCostsCount}
        />
      ),
    },
  ];

  const tabs = tabDefinitions
    .filter((t) => t.visible !== false)
    .map((t, index) => ({
      title: t.title,
      index,
      component: equals(currentTab, index) && t.render(index),
    }));

  if (!asset || (!spareParts && isSparePartEnabled)) return <Loader />;
  return (
    <>
      <PageContentWrapper heading={`${asset?.name}`} loading={!asset} error={errorAsset}>
        <TabPanel
          selectedIndex={currentTab}
          dataSource={tabs}
          itemRender={(a) => a.component}
          activeStateEnabled={true}
          onTitleClick={(e: any) => setCurrentTab(e.itemIndex)}
          deferRendering={false}
          focusStateEnabled={false}
        />
      </PageContentWrapper>
      <Prompt
        // smazat local storage při opuštění stránky
        message={(location, action) => {
          console.log(location, action);
          if (action === 'PUSH') localStorage.removeItem(STORAGE_KEYS.CONTEXT_STATE);
          return true;
        }}
      />
    </>
  );
}
export default AssetsEdit;
